import { useEffect, useState } from 'react';
import type { UpdateTurnstileSettingsInput } from '@klaro7/validators';
import { Badge, Button, Input, Spinner } from '@klaro7/ui';

import { usePinGate } from '@/components/PinGate';
import { useTurnstileSettings, useUpdateTurnstileSettings } from '../hooks/useServicesSettings';

function extractErrorDetail(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = err as {
      response?: {
        data?: { error?: { message?: string }; message?: string };
        status?: number;
        statusText?: string;
      };
    };
    const apiMsg = resp.response?.data?.error?.message || resp.response?.data?.message;
    const status = resp.response?.status;
    if (apiMsg) return `[${status}] ${apiMsg}`;
    if (status) return `HTTP ${status}: ${resp.response?.statusText || 'Errore sconosciuto'}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Cloudflare Turnstile (anti-bot su /auth/register) — super-admin only.
 * secretKey arriva mascherata dal server e viene inclusa nel PUT solo quando
 * l'admin digita un nuovo valore (mirrors Stripe/SMS/WebRTC), così un segreto
 * salvato non viene mai sovrascritto con la sua maschera.
 */
export function TurnstileSettingsSection() {
  const { requirePin, pinDialog } = usePinGate();
  const { data: settings, isLoading } = useTurnstileSettings(true);
  const updateMutation = useUpdateTurnstileSettings();

  const [siteKey, setSiteKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [savedSecretKey, setSavedSecretKey] = useState('');

  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (settings) {
      setSiteKey(settings.siteKey ?? '');
      setSecretKey(settings.secretKey ?? '');
      setEnabled(settings.enabled ?? false);
      setSavedSecretKey(settings.secretKey ?? '');
    }
  }, [settings]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.type === 'error' ? 15000 : 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  async function handleSave() {
    const payload: UpdateTurnstileSettingsInput = { siteKey, enabled };
    if (secretKey !== savedSecretKey) {
      payload.secretKey = secretKey;
    }
    try {
      await updateMutation.mutateAsync(payload);
      setToast({ type: 'success', message: 'Configurazione Turnstile salvata con successo' });
    } catch (err) {
      setToast({ type: 'error', message: `Errore nel salvataggio Turnstile: ${extractErrorDetail(err)}` });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">Anti-bot registrazione (Turnstile)</h3>
            <p className="text-xs text-neutral-600">Cloudflare Turnstile su /auth/register (solo super admin)</p>
          </div>
        </div>
        <Badge variant={enabled ? 'success' : 'default'}>
          {enabled ? 'Attivo' : 'Disattivato'}
        </Badge>
      </div>

      <div className="space-y-4 p-5">
        {toast && (
          <div
            className={`rounded-lg border px-4 py-2.5 text-sm font-medium ${
              toast.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {toast.message}
          </div>
        )}

        <Input
          label="Site Key"
          value={siteKey}
          onChange={(e) => setSiteKey(e.target.value)}
          placeholder="0x4AAAAAAA..."
          helperText="Chiave pubblica del widget Cloudflare Turnstile. Salvata in chiaro, usata dal form di registrazione."
        />

        <Input
          label="Secret Key"
          type="password"
          value={secretKey}
          onChange={(e) => setSecretKey(e.target.value)}
          placeholder="••••••••••••••••"
          helperText="Chiave segreta, cifrata. Lascia invariato il valore mascherato per non sovrascriverla."
        />

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
          />
          Abilitato
        </label>

        <p className="rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3 text-xs text-neutral-600">
          Crea un widget Turnstile su{' '}
          <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noopener noreferrer" className="underline">
            dash.cloudflare.com → Turnstile
          </a>
          {' '}(gratuito, nessuna carta richiesta). Finché disattivato o senza chiavi, la
          registrazione non richiede alcuna verifica anti-bot.
        </p>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-4">
          {pinDialog}
          <Button onClick={() => requirePin(handleSave)} loading={updateMutation.isPending}>
            Salva Turnstile
          </Button>
        </div>
      </div>
    </div>
  );
}
